import React, { useEffect, useRef } from 'react';
import { Col, FormInstance, Row } from 'antd';
import ProForm, { ProFormText } from '@ant-design/pro-form';
import { useIntl } from 'umi';
import classNames from 'classnames';
import LeadLocale from '@/components/Lead/LeadLocale';
import LeadSalutation from '@/components/Lead/LeadSalutation';
import PhoneNumberInput from '@/components/format/PhoneNumberInput';
import { F } from '@/utils/intl';
import { ContactModel, ContactPayload } from '@/typings/models/Contact';
import RoleInput from './RoleInput';
import styles from './index.module.less';

export type ContactFieldName = keyof ContactPayload | 'company_name';

export type ContactFieldValues = Partial<ContactModel> & { company_name?: string | null };

/** A contact needs one of these (the backend refuses a contact without). */
export const CONTACT_IDENTITY_FIELDS = ['firstname', 'lastname', 'email', 'phone'] as const;

export const hasContactIdentity = (values: Record<string, unknown> = {}) =>
  CONTACT_IDENTITY_FIELDS.some((field) => String(values[field] ?? '').trim());

type ContactFieldsProps = {
  form: FormInstance;
  /** The saved values; inline mode only saves fields that differ from them. */
  values: ContactFieldValues;
  /** Inline editing: a changed, valid field is saved as soon as it is left. */
  onFieldSave?: (field: ContactFieldName, value: string | null) => void;
  /** Fields that must not be empty, e.g. the partner's required customer fields. */
  requiredFields?: ContactFieldName[];
  showRole?: boolean;
  roleSuggestions?: string[];
  /** Field to flag with a warning border (duplicate check). */
  warningField?: 'email' | 'phone';
  disabled?: boolean;
  size?: 'small' | 'middle';
};

const toFormValues = (values: ContactFieldValues): Record<string, unknown> => ({
  firstname: values.firstname || '',
  lastname: values.lastname || '',
  email: values.email || '',
  phone: values.phone || undefined,
  gender: values.gender || undefined,
  locale: values.locale || undefined,
  role: values.role || '',
});

const ContactFields: React.FC<ContactFieldsProps> = ({
  form,
  values,
  onFieldSave,
  requiredFields = [],
  showRole,
  roleSuggestions = [],
  warningField,
  disabled,
  size = 'small',
}) => {
  const intl = useIntl();

  const previous = useRef<Record<string, unknown> | null>(null);
  useEffect(() => {
    const next = toFormValues(values);
    const before = previous.current;
    previous.current = next;
    // A save landing while the user types elsewhere must not reset that other
    // field: only fields still showing the previously saved value follow along.
    const untouched = before
      ? Object.fromEntries(
          Object.entries(next).filter(([field]) => form.getFieldValue(field) === before[field]),
        )
      : next;
    form.setFieldsValue(untouched);
  }, [form, values]);

  const save = async (field: ContactFieldName, value?: string | null) => {
    if (!onFieldSave) return;
    const next = typeof value === 'string' ? value.trim() : value ?? null;
    if ((next || null) === ((values[field] as string | null | undefined) || null)) return;
    try {
      await form.validateFields([field]);
      onFieldSave(field, next || null);
    } catch {
      // the field shows its own validation message
    }
  };

  const required = (field: ContactFieldName) => requiredFields.includes(field);
  // Emptying the last of name, email and phone would leave a contact nobody can identify.
  const identityRule = {
    validator: () =>
      hasContactIdentity(form.getFieldsValue([...CONTACT_IDENTITY_FIELDS]))
        ? Promise.resolve()
        : Promise.reject(
            new Error(
              intl.formatMessage({
                id: 'component.contacts.identityRequired',
                defaultMessage: 'Enter a name, email or phone.',
              }),
            ),
          ),
  };
  const rulesFor = (field: ContactFieldName, extra: object[] = []) => [
    { required: required(field) },
    ...((CONTACT_IDENTITY_FIELDS as readonly string[]).includes(field) ? [identityRule] : []),
    ...extra,
  ];

  const textField = (field: ContactFieldName, label: React.ReactNode, extraRules: object[] = []) => (
    <ProFormText
      label={label}
      name={field}
      rules={rulesFor(field, extraRules)}
      fieldProps={{ onBlur: (event) => save(field, event.target.value) }}
    />
  );

  return (
    <ProForm
      form={form}
      autoComplete="off"
      size={size}
      submitter={false}
      variant="underlined"
      layout="vertical"
      disabled={disabled}
      className={styles.contactFields}
    >
      <Row gutter={[16, 0]}>
        {/* Salutation sits with the name, narrow: it holds "Mr" or "Ms". */}
        <Col xs={8} sm={4}>
          <ProForm.Item
            label={<F id="customer.salutation" defaultMessage="Salutation" />}
            name="gender"
            rules={rulesFor('gender')}
          >
            <LeadSalutation onChange={(value) => save('gender', value)} />
          </ProForm.Item>
        </Col>
        <Col xs={16} sm={10}>
          {textField('firstname', intl.formatMessage({ id: 'pages.customers.firstName', defaultMessage: 'First Name' }))}
        </Col>
        <Col xs={24} sm={10}>
          {textField('lastname', intl.formatMessage({ id: 'pages.customers.lastName', defaultMessage: 'Last Name' }))}
        </Col>
        <Col xs={24} sm={12} className={classNames({ [styles.warningField]: warningField === 'email' })}>
          {textField('email', intl.formatMessage({ id: 'common.attribute.email', defaultMessage: 'Email' }), [
            { type: 'email' },
          ])}
        </Col>
        <Col xs={24} sm={12} className={classNames({ [styles.warningField]: warningField === 'phone' })}>
          <ProForm.Item
            label={intl.formatMessage({ id: 'common.attribute.phone', defaultMessage: 'Phone' })}
            name="phone"
            rules={rulesFor('phone')}
          >
            <PhoneNumberInput onChange={(e164) => save('phone', e164)} />
          </ProForm.Item>
        </Col>
        <Col xs={24} sm={12}>
          <ProForm.Item
            label={<F id="pages.leads.filters.locale.placeholder" defaultMessage="Language" />}
            name="locale"
            rules={rulesFor('locale')}
          >
            <LeadLocale
              onChange={(value) => save('locale', value)}
              selectProps={{ bordered: true, allowClear: false }}
            />
          </ProForm.Item>
        </Col>
        {showRole && (
          <Col xs={24} sm={12}>
            <ProForm.Item label={<F id="entity.role.singular" defaultMessage="Role" />} name="role">
              <RoleInput suggestions={roleSuggestions} onCommit={(value) => save('role', value)} />
            </ProForm.Item>
          </Col>
        )}
      </Row>
    </ProForm>
  );
};

export default ContactFields;
